import { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Smartphone } from 'lucide-react';
import AuthImage from './AuthImage';
import { numberedQuestion, translateKo, inspectInfoText } from '../utils/checklistKoEn';

/**
 * Phone-sized preview of how a checklist item looks on the worker app.
 * Last item: Done stays in the preview (end screen) — does not dismiss the overlay.
 */
export default function AppChecklistPreview({ ssCode, ssName, items, onClose }) {
  const list = Array.isArray(items) ? items : [];
  const [idx, setIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [finished, setFinished] = useState(false);
  const total = list.length;
  const item = total ? list[Math.min(idx, total - 1)] : null;
  const q = item
    ? numberedQuestion(item.question || item.inspectionItem || item.text || '', item.order || item.item_order, idx)
    : '';
  const category = translateKo(item?.category || item?.section || 'Check') || 'Check';
  const info = item ? inspectInfoText(item) : '';
  const unit = item?.unit && item.unit !== '-' && item.unit !== '—' ? item.unit : '';
  const showM = item?.value_type === 'NUM' || item?.show_measurement || !!unit;
  const last = total > 0 && idx >= total - 1;
  const imgs = Array.isArray(item?.guidance_images) ? item.guidance_images : [];

  const goPrev = () => {
    setFinished(false);
    setIdx((i) => Math.max(0, i - 1));
    setResult(null);
  };

  const goNext = () => {
    if (!total) return;
    if (last) {
      setFinished(true);
      return;
    }
    setIdx((i) => i + 1);
    setResult(null);
  };

  const restart = () => {
    setFinished(false);
    setIdx(0);
    setResult(null);
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        className="flex flex-col sm:flex-row gap-4 items-center sm:items-stretch max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-[22rem] bg-slate-900 rounded-[1.75rem] p-2 shadow-2xl shrink-0">
          <div className="bg-slate-50 rounded-[1.35rem] overflow-hidden flex flex-col h-[min(38rem,82vh)]">
            <header className="px-3 py-3 bg-blue-700 text-white flex items-center gap-2 shrink-0">
              <span className="p-1 text-white text-lg leading-none">‹</span>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-bold truncate">Inspect unit</h2>
                <p className="text-[11px] text-blue-100 truncate">
                  {ssCode}
                  {ssName ? ` · ${ssName}` : ''} · UNIT-01
                </p>
              </div>
              <span className="text-xs bg-blue-600 px-2 py-1 rounded">
                {total ? `${finished ? total : idx + 1}/${total}` : '0/0'}
              </span>
            </header>
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
              {!total ? (
                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-semibold text-slate-800">No questions yet</p>
                  <p className="text-xs text-slate-500">
                    Add questions under <strong>Add question</strong> or <strong>Edit</strong>, then Save. Open this
                    preview again to walk through the cards.
                  </p>
                </div>
              ) : finished ? (
                <div className="bg-white border border-emerald-200 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-emerald-800">End of checklist</p>
                  <p className="text-xs text-slate-600">
                    You reached the last of {total} question{total === 1 ? '' : 's'} for {ssCode || 'this SS'}. This is
                    only a preview — use the X or Close to return to checklist setup.
                  </p>
                  <button
                    type="button"
                    onClick={restart}
                    className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold"
                  >
                    Start again from question 1
                  </button>
                </div>
              ) : !item ? (
                <p className="text-sm text-slate-500 p-4">No questions to preview. Add or load items first.</p>
              ) : (
                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">{category}</p>
                  <p className="text-base font-semibold text-slate-900">{q}</p>
                  {info ? (
                    <div className="rounded-lg bg-sky-50 border border-sky-100 px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700">Info</p>
                      <p className="text-xs text-slate-800 mt-0.5 whitespace-pre-wrap">{info}</p>
                    </div>
                  ) : null}
                  {imgs.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {imgs.map((g, gi) => (
                        <AuthImage
                          key={g.filename || gi}
                          filename={g.filename}
                          alt={g.caption || 'Guidance'}
                          className="w-24 h-24 object-cover rounded-lg border"
                        />
                      ))}
                    </div>
                  ) : null}
                  {showM ? (
                    <label className="block text-xs text-slate-600 mt-2">
                      Measurement {unit ? `(${unit})` : ''}
                      <input
                        type="text"
                        readOnly
                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-50"
                        placeholder="Enter value"
                      />
                    </label>
                  ) : null}
                  <p className="text-[11px] text-slate-500 pt-1">
                    Preview only — Pass / Fail / N/A stay fixed above Previous / Next on the phone.
                  </p>
                </div>
              )}
            </div>
            <div className="p-3 border-t bg-white space-y-2 shrink-0">
              {!finished && total > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {['PASS', 'FAIL', 'NA'].map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setResult(r)}
                      className={`py-3 rounded-xl font-bold text-sm ${
                        result === r
                          ? r === 'PASS'
                            ? 'bg-emerald-600 text-white'
                            : r === 'FAIL'
                              ? 'bg-red-600 text-white'
                              : 'bg-slate-700 text-white'
                          : r === 'PASS'
                            ? 'bg-emerald-100 text-emerald-800'
                            : r === 'FAIL'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {r === 'NA' ? 'N/A' : r === 'PASS' ? 'Pass' : 'Fail'}
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={!total || (idx === 0 && !finished)}
                  onClick={goPrev}
                  className="py-3 rounded-lg bg-slate-100 font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                {finished ? (
                  <button
                    type="button"
                    onClick={onClose}
                    className="py-3 rounded-lg bg-slate-800 text-white font-semibold text-sm"
                  >
                    Close preview
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!total}
                    onClick={goNext}
                    className={`py-3 rounded-lg text-white font-semibold text-sm inline-flex items-center justify-center gap-1 ${
                      last ? 'bg-emerald-600' : 'bg-blue-600'
                    }`}
                  >
                    {last ? 'Done' : 'Next'}
                    {!last ? <ChevronRight className="w-4 h-4" /> : null}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="sm:w-64 text-white space-y-3 max-w-sm">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Worker app preview
            </p>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/10" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-white/80 leading-relaxed">
            Preview of the worker card. On a building Checklists page, use <strong>Save to this building</strong> so
            these questions go to this building’s units only (shared SS original stays unchanged). Then Sync on the
            phone.
          </p>
          {!total ? (
            <p className="text-xs text-amber-200 leading-relaxed">
              This SS has no questions yet — add some, Save, then preview again.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
